// share-service.js
//
// Single entry point for "share this URL" and "save / share this image" so
// the call sites (ResultStep, DesignDetail, ShareView, CollectionPage,
// Invite) don't each have to re-decide between Web Share API and the
// Capacitor native plugins.
//
// Web path: navigator.share when available; clipboard fallback.
// Native path (Sprint A 5단계): Capacitor Share + Filesystem. Images get
// written to the cache directory first, then handed to the native share
// sheet — iOS exposes a "Save Image" action that goes to Photos, and the
// usual Messages / Mail / AirDrop targets all work the same way.

import { isNativeApp } from './platform-service.js';

// --- Web helpers ------------------------------------------------------------

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const comma = String(result).indexOf(',');
      resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function downloadBlobAsFile(blob, filename) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

// --- Public API -------------------------------------------------------------

// Share a link (no image attached). Returns true if a share UI was actually
// presented; false if we fell back to copying to the clipboard.
export async function shareLink({ title, text, url }) {
  if (isNativeApp()) {
    const { Share } = await import('@capacitor/share');
    try {
      // iOS 는 text 와 url 을 UIActivityViewController 에 **별도 항목**으로
      // 넘긴다. 받는 앱이 첫 항목만 가져가면 문구만 가고 링크는 사라진다 —
      // 프로필 공유가 "Take a look at @...'s designs on archelier." 만 붙고
      // URL 도 앱 연결도 없던 이유다.
      // 둘 다 있으면 한 덩어리 문자열로 합쳐 항목을 하나로 만든다. iOS 가
      // 본문 안의 URL 을 알아서 링크로 만들어 주므로 어디로 보내든 링크가
      // 같이 간다. (url 만 있는 호출부는 그대로 — 그쪽은 원래 잘 됐다.)
      const payload = text && url
        ? { title, text: `${text}\n${url}`, dialogTitle: title || 'Share' }
        : { title, text, url, dialogTitle: title || 'Share' };
      await Share.share(payload);
      return true;
    } catch (err) {
      if (err?.message?.toLowerCase?.().includes('canceled')) return false;
      throw err;
    }
  }
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false;
      // Fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    /* swallow — caller decides whether to show "copied" UI */
  }
  return false;
}

// Save / share an image. On native we write to the cache dir then invoke the
// native share sheet (iOS users pick "Save Image" to put it in Photos). On web
// we trigger the standard download anchor.
export async function shareOrDownloadImage({ blob, filename, title, text }) {
  if (isNativeApp()) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);
    const data = await blobToBase64(blob);
    const writeRes = await Filesystem.writeFile({
      path: filename,
      data,
      directory: Directory.Cache,
    });
    try {
      await Share.share({
        title,
        text,
        url: writeRes.uri,
        dialogTitle: title || 'Share image',
      });
      return 'shared';
    } catch (err) {
      if (err?.message?.toLowerCase?.().includes('canceled')) return 'cancelled';
      throw err;
    }
  }
  downloadBlobAsFile(blob, filename);
  return 'downloaded';
}
